/**
 * src/components/dashboard/TablaFrescura.jsx
 * Qué tiene cada tenant por actualizar, en una tabla bajo el parte.
 *
 * Manuel, 08-09-2026: «a veces tenemos tenants sin refrescar su dashboard, y
 * con tantos cambios que vamos haciendo es difícil tener este seguimiento».
 * Hasta hoy eso solo se sabía leyendo los logs del worker en Railway.
 *
 * Dos decisiones de presentación, las dos con su porqué:
 *
 * 1. **El veredicto se ve, no se pide.** La celda dice «hace 9 d · toca cada
 *    24h», no un icono a secas con la explicación escondida. El cerrojo de
 *    `Pista` es explícito: los globos son para salvaguardas y definiciones,
 *    nunca para un dato que cambie la decisión — y aquí el dato ES la decisión
 *    (qué job hay que lanzar). Las `Pista` de esta pantalla están solo en las
 *    cabeceras, explicando cómo se mide cada columna, que sí es una definición.
 *
 * 2. **Ámbar, no rojo.** El rojo de este panel es el de las alertas del parte,
 *    que significa «algo está roto». Aquí no hay nada roto: hay trabajo
 *    pendiente. Con cuatro columnas y varios tenants, pintarlo en rojo haría
 *    que una tarde de tareas normales se leyera como una avería mayor — y a la
 *    tercera vez que eso pasa, el rojo del parte deja de significar nada.
 *
 * 3. **Y por eso mismo, desde el 24-09, la columna «Censo» lleva DOS líneas.**
 *    Su ámbar vuelve cada mañana por diseño —el censo corre a las 02:00 y el
 *    pipeline trae OCs después, así que «OCs nuevas sin censar» es verdad casi
 *    todos los días—, y un ámbar que está puesto siempre no avisa de nada. La
 *    primera línea sigue diciendo QUÉ le falta; la segunda dice si alguien lo va
 *    a atender, y esa es la que distingue la noche normal del tenant olvidado.
 *    Solo esa segunda línea puede ponerse en rojo, y no contradice la regla de
 *    arriba: el rojo no dice «hay trabajo pendiente» —eso es el ámbar de al
 *    lado— sino «el que tenía que hacerlo lleva días sin hacerlo», que sí es
 *    algo roto. El backend manda las palabras; aquí solo se decide el color.
 */
import { useNavigate } from 'react-router-dom'

import { Card } from '../ui/Card'
import { Pista } from '../ui/Pista'
import { Table } from '../ui/Table'

// Cómo se pinta cada veredicto. El texto de la celda lo manda el backend; aquí
// solo se decide el color y la forma de la marca.
const MARCA = {
  al_dia:   { color: 'var(--color-ok)',     relleno: true,  tenue: false },
  atrasado: { color: 'var(--color-warn)',   relleno: true,  tenue: false },
  apagado:  { color: 'var(--color-text-3)', relleno: false, tenue: true },
  nunca:    { color: 'var(--color-text-3)', relleno: false, tenue: true },
}

// El color de la segunda línea de «Censo». Va aparte de MARCA porque mide otra
// cosa: MARCA dice si el dato está viejo, esto dice si alguien lo está atendiendo.
//
// ⚠️ `prevista` va en gris a propósito. Es la situación NORMAL —el reponedor lo
// tiene en la cola— y pintarla con color la convertiría en un segundo semáforo
// encendido a diario, que es exactamente el problema que esto viene a resolver.
const ATENCION = {
  prevista:    'var(--color-text-3)',
  desatendida: 'var(--color-error)',
  apagada:     'var(--color-warn)',
  sin_fecha:   'var(--color-warn)',
}

// La regla de cada columna, en la cabecera. Son DEFINICIONES —cómo se mide—,
// que es justo para lo que existe `Pista`.
const REGLA = {
  precios: (u) => (
    <>Corre según la frecuencia que tenga puesta el tenant (24h / 48h / semanal).
      Se le llama atrasado cuando pasa su plazo más {u.gracia_h} h de gracia: el
      worker atiende de uno en uno y se han medido hasta 2 h de cola, así que sin
      ese margen un tenant diario saldría en ámbar casi cada día por haber
      terminado media hora más tarde que ayer.</>
  ),
  ocs: () => (
    <>Misma regla que Precios, con la frecuencia de OCs del tenant. Suele ser
      semanal.</>
  ),
  catalogo: (u) => (
    <>El catálogo es la ENTRADA del scraper de precios, y vale {u.catalogo_dias} días.
      Nadie lo programa: se lanza a mano. A un tenant sin precios programados no
      se le exige —su catálogo no alimentaría nada—, y por eso sale «no aplica»
      en vez de en ámbar.</>
  ),
  dashboard: () => (
    <>Esta columna no mira un reloj: mira un desnivel. El HTML se rehornea al
      final de cualquier job MENOS el de «Solo catálogo», así que está atrasado
      cuando hay datos más nuevos que el último horneado. Desde el 08-09 el
      worker encola ese horneado solo detrás del catálogo, así que esta casilla
      se cierra sola: si sigue en ámbar un buen rato, es que el segundo job no
      llega a correr.</>
  ),
  censo: (u, at) => (
    <>Tampoco tiene reloj. Lo vence que el pipeline corra DESPUÉS del censo:
      entonces hay órdenes nuevas que el censo no ha visto y sus cifras se
      quedaron con las de antes. «Sin censar» sale en gris porque puede ser un
      tenant que aún no ha tenido su primer run de OCs.
      {'\u00A0'}
      <br /><br />
      <strong>La segunda línea dice quién lo atiende.</strong> El reponedor corre
      una pasada de censo a las {at?.hora_pasada ?? '02:00'} de Chile y atiende{' '}
      {at?.tope ?? '?'} por noche, empezando por el que lleva más tiempo sin
      censar. Con {at?.candidatos ?? 0} en la cola, a cada uno le toca como muy
      tarde cada {at?.limite_noches ?? '?'} noches; pasado ese plazo la línea se
      pone en rojo, porque entonces no es que espere turno: es que la rotación no
      está girando.
      {at?.activo === false && (
        <> Ahora mismo el censo automático está <strong>apagado</strong>
          ({'REPONEDOR_CENSO_ACTIVO'} no vale 1), así que nadie va a quitar
          estos ámbares.</>
      )}
      {at?.averia && <> No se pudo calcular quién lo atiende: {at.averia}.</>}</>
  ),
  sonda: () => (
    <>Se enseña, pero no se exige. Tarda minutos por tenant y consulta doce
      tiendas del portal, y es exploratoria: busca dónde vive un convenio del que
      no sabíamos nada, y para muchos tenants puede no merecer la pena nunca. Por
      eso «sin sondear» va en gris. Solo se pone en ámbar el caso cerrable: se
      volvió a censar después de sondear, así que hay convenios nuevos que la
      sonda no llegó a ver.</>
  ),
}

function Celda({ celda }) {
  if (!celda) return <span style={{ color: 'var(--color-text-3)' }}>—</span>
  const m = MARCA[celda.estado] || MARCA.nunca
  const at = celda.atencion
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '1px' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span
          aria-hidden="true"
          style={{
            width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0,
            background: m.relleno ? m.color : 'transparent',
            border: m.relleno ? 'none' : `1px solid ${m.color}`,
          }}
        />
        <span style={{
          fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)',
          color: m.tenue ? 'var(--color-text-3)' : 'var(--color-text-2)',
          whiteSpace: 'nowrap',
        }}>
          {celda.texto}
        </span>
      </span>
      {/* La segunda línea. ⚠️ SIN `nowrap`, y medido antes de escribirlo: esta
          tabla vive dentro de un `overflowX: auto`, donde una columna que no cabe
          no sale apretada, sale FUERA y no se ve — así se perdió la columna
          «Suscripción» del Embudo el mismo día. Con las frases de hoy (28
          caracteres la más larga) `nowrap` todavía cabría a 1366 px; con una de
          58 la tabla pedía 1267 px en un recuadro de 1088 y dejaba «Censo» y
          «Sonda» fuera. El margen no lo da el CSS, lo da el largo de la frase:
          envolviendo, alargarla solo cuesta alto, que aquí sobra.

          El porqué largo va en una `Pista` y NO en el `title` del navegador:
          `Pista.jsx` deja escritos los cinco motivos por los que ese `title` no
          vale (tarda, no se puede copiar, no existe al tocar, no se puede
          probar). Y cabe dentro de su cerrojo: lo que decide —«lleva 9 noches sin
          censarse»— se ve; lo que se pide es por qué esas noches son demasiadas,
          que es una definición. En `prevista` el globo dice la letra pequeña que
          la línea no puede prometer: que es la previsión de la próxima pasada y
          no una promesa. */}
      {at && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)',
          paddingLeft: 'calc(7px + var(--space-2))',
        }}>
          <span style={{
            fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)',
            color: ATENCION[at.estado] || ATENCION.prevista,
            lineHeight: 1.3,
          }}>
            {at.texto}
          </span>
          {at.detalle && <Pista etiqueta={at.texto} contenido={at.detalle} />}
        </span>
      )}
    </span>
  )
}

export function TablaFrescura({ datos, cargando, error }) {
  const navigate = useNavigate()
  const umbrales = datos?.umbrales ?? { catalogo_dias: 7, gracia_h: 6 }
  // ⚠️ Los números del reponedor se LEEN de la respuesta, no se repiten aquí. El
  // tope de censos por noche es una variable de entorno que Manuel puede subir:
  // escrito también en el frontend, el día que suba, la pantalla seguiría
  // explicando el viejo y nadie lo notaría (§4).
  const atencion = datos?.atencion_censo

  const columnas = [
    {
      key: 'nombre', label: 'Tenant', width: '190px',
      render: (v, row) => (
        <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
          <span>{v}</span>
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
            color: 'var(--color-text-3)', wordBreak: 'break-all',
          }}>
            {row.cliente_id}
          </span>
        </span>
      ),
    },
    ...(datos?.columnas ?? []).map(col => ({
      key: col.clave, width: '160px',
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          {col.rotulo}
          <Pista etiqueta={col.rotulo}
                 contenido={(REGLA[col.clave] || (() => null))(umbrales, atencion)} />
        </span>
      ),
      render: (_v, row) => <Celda celda={row.celdas?.[col.clave]} />,
    })),
  ]

  // §5: una tabla que se queda vacía sin decir por qué es indistinguible de una
  // tabla sin pendientes, y las dos lecturas llevan a sitios opuestos.
  if (error) {
    return (
      <Card title="Frescura por tenant">
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error)' }}>
          {/* El detalle del backend primero: el endpoint devuelve el nombre
              de la avería («TypeError: ...»), y `error.message` a secas solo
              diría «status code 500», que no permite empezar por ningún sitio. */}
          No se pudo leer la frescura:{' '}
          {error?.response?.data?.detail || String(error?.message || error)}
        </div>
      </Card>
    )
  }

  const conPendiente = datos?.resumen?.tenants_con_pendiente ?? 0
  const total = datos?.resumen?.tenants ?? 0

  // Los que el reponedor debería haber censado y no ha censado, contados para la
  // cabecera. Sin esto habría que recorrer la tabla celda a celda para dar con la
  // única línea roja, y con doce tenants eso no se hace: el aviso que solo se ve
  // buscándolo no avisa (§10).
  const desatendidos = (datos?.filas ?? []).filter(
    f => f.celdas?.censo?.atencion?.estado === 'desatendida').length

  return (
    <Card
      title="Frescura por tenant"
      subtitle={
        cargando ? 'midiendo…'
          : [
              conPendiente
                ? `${conPendiente} de ${total} tenants tienen algo pendiente`
                : `los ${total} tenants están al día`,
              desatendidos
                ? `${desatendidos} ${desatendidos === 1 ? 'lleva' : 'llevan'} noches sin censarse`
                : null,
            ].filter(Boolean).join(' · ')
      }
      padding="0"
    >
      <Table
        columns={columnas}
        data={datos?.filas ?? []}
        loading={cargando}
        claveFila={row => row.cliente_id}
        onRowClick={row => navigate(`/ejecuciones/${row.cliente_id}`)}
        emptyMessage="Sin tenants activos"
      />
    </Card>
  )
}
